import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Pagination } from '@components'
import Breadcrumb from '@components/Breadcrumb'
import Button from '@components/Button'
import Divider from '@components/Divider'
import EmptyRow from '@components/EmptyRow'
import Flex from '@components/Flex'
import PageTitle from '@components/PageTitle'
import ReviewAlerts from '@components/ReviewAlerts'
import Spinner from '@components/Spinner'
import Table from '@components/Table'
import Td from '@components/Td'
import Template from '@components/Template'
import Tr from '@components/Tr'
import { successMessages } from '@constants/feedbackMessages'
import {
	AiGenerationStatus,
	describeGenerationStatus,
} from '@models/aiGeneration.model'
import { aiGenerationApi } from '@services/AiGenerationApi'
import { notificationCreators } from '@store/ducks/notification'
import { useAppDispatch } from '@store/hooks'
import { formatGenerationDate, formatGenerationDuration } from '@utils/helpers'
import {
	createBreadcrumbLink,
	useCreateBreadcrumb,
	useQuery,
} from '@utils/hooks'

import GenerationStatusBadge from '../StatusBadge'

const RUNNING: AiGenerationStatus[] = ['created', 'queued', 'processing']

// Com a conversão andando a tela se atualiza sozinha: as questões chegam uma a
// uma ao longo de minutos, e quem espera não deve precisar recarregar.
const REFRESH_WHILE_RUNNING_MS = 8_000

function GenerationDetail() {
	const { id } = useParams<{ id: string }>()
	const navigate = useNavigate()
	const dispatch = useAppDispatch()

	const query = useQuery()
	const [showHistory, setShowHistory] = useState(false)

	// A paginação navega pela query string, como no resto do admin: assim o
	// endereço da página continua sendo o que se compartilha.
	const page = Number(query.get('page')) || 1

	const generationId = id ?? ''

	const baseUrl = '/conversoes'

	/*
	 * Ainda vem questão por aí?
	 *
	 * Não basta olhar o estado: o despacho fica "concluída" quando o caderno
	 * termina de ser lido, e as questões continuam chegando uma a uma depois
	 * disso. Parando de atualizar ali, a tela congelava com metade da prova e
	 * parecia cache.
	 *
	 * Mora em estado porque decide de quanto em quanto tempo a própria consulta
	 * que o alimenta volta a buscar: começa ligado e se desliga quando o último
	 * número bate.
	 */
	const [stillArriving, setStillArriving] = useState(true)

	const { data: generation } = aiGenerationApi.useShowQuery(
		{ id: generationId },
		{
			skip: !id,
			pollingInterval: stillArriving ? REFRESH_WHILE_RUNNING_MS : 0,
			refetchOnMountOrArgChange: true,
		}
	)

	useEffect(() => {
		if (!generation) {
			return
		}

		setStillArriving(
			RUNNING.includes(generation.status) ||
				generation.receivedQuestions < generation.totalQuestions
		)
	}, [generation])

	/*
	 * As questões chegam uma a uma ao longo de minutos, e quem está com a tela
	 * aberta não deve precisar recarregar. O intervalo é do próprio RTK Query,
	 * e não um `setInterval` ao lado: trocando de página, ele passa a atualizar
	 * a página nova. Um relógio ao lado continuaria buscando a página onde a
	 * pessoa não está mais, e a que ela vê ficaria servida do cache.
	 */
	const {
		data: questions,
		isLoading: loadingQuestions,
		isFetching: fetchingQuestions,
	} = aiGenerationApi.useQuestionsQuery(
		{ id: generationId, page },
		{
			skip: !id,
			pollingInterval: stillArriving ? REFRESH_WHILE_RUNNING_MS : 0,
			refetchOnMountOrArgChange: true,
		}
	)

	const { data: history } = aiGenerationApi.useHistoryQuery(
		{ id: generationId },
		{ skip: !id || !showHistory }
	)

	const [cancelGeneration] = aiGenerationApi.useCancelMutation()
	const [retryGeneration] = aiGenerationApi.useRetryMutation()

	/*
	 * O número sozinho não diz de que prova se trata, e é pelo arquivo que quem
	 * abriu o pedido a reconhece. Fica depois da consulta porque depende do que
	 * ela traz; até chegar, o número já serve de trilha.
	 */
	const allPages = useMemo(
		() => [
			createBreadcrumbLink('Conversão de provas', baseUrl, 1),
			createBreadcrumbLink(
				generation?.fileName
					? `Conversão ${id} · ${generation.fileName}`
					: `Conversão ${id}`,
				`${baseUrl}/${id}`,
				2
			),
		],
		[id, generation?.fileName]
	)

	const pages = useCreateBreadcrumb(allPages)

	const isRunning = Boolean(generation && RUNNING.includes(generation.status))

	if (!generation) {
		return (
			<Template>
				<div className='flex justify-center mt-10'>
					<Spinner size='md' />
				</div>
			</Template>
		)
	}

	const examLine =
		[
			generation.examBank,
			generation.examYear,
			generation.examPosition,
			generation.examOrganization,
		]
			.filter(Boolean)
			.join(' · ') || 'Prova não identificada pela conversão'

	/*
	 * O quanto já foi feito sai dos contadores da própria geração.
	 *
	 * As questões chegam uma a uma ao longo de minutos, e é o que chegou que
	 * mede o andamento. Antes de o caderno ser lido não se sabe quantas são, e
	 * uma barra sem tamanho é pior que barra nenhuma — então ela só aparece
	 * quando há um total contra o que comparar.
	 */
	const percentage =
		generation.totalQuestions > 0
			? Math.min(
					100,
					Math.round(
						(generation.receivedQuestions / generation.totalQuestions) * 100
					)
			  )
			: null

	return (
		<Template>
			<Breadcrumb pages={pages} />
			<PageTitle title={generation.fileName} />

			<div className='mt-6 bg-white rounded-sm shadow-5 p-6'>
				<Flex className='justify-between items-start flex-wrap gap-4'>
					<div>
						<p className='text-gray'>{examLine}</p>

						<p className='mt-2 text-dark'>
							{generation.receivedQuestions} questões recebidas
							{generation.totalQuestions > 0
								? ` de ${generation.totalQuestions} lidas no caderno`
								: ''}
						</p>

						{/* Quando acabou e quanto levou. O histórico ao lado conta a vida
						    do despacho, que termina em um segundo; o que demora é a
						    conversão, e era ela que não tinha hora de término em lugar
						    nenhum da tela. */}
						{!isRunning && generation.durationMs > 0 && (
							<p className='mt-2 text-sm text-gray'>
								Concluída em {formatGenerationDate(generation.updatedAt)}, após{' '}
								{formatGenerationDuration(generation.durationMs)} de conversão
							</p>
						)}

						{generation.answerKeyVariant && (
							<p className='mt-1 text-sm text-gray'>
								Gabarito: {generation.answerKeyVariant}
							</p>
						)}

						{/* O microserviço fora do ar não esconde o pedido: o aviso some,
						    o resto da tela continua legível. */}
						{isRunning && generation.dispatch === null && (
							<p className='mt-2 text-sm text-yellow'>
								Não foi possível falar com o microserviço agora.
							</p>
						)}

						{generation.dispatch?.isStale && (
							<p className='mt-2 text-sm text-yellow'>
								Sem sinal de vida do processamento há alguns minutos.
							</p>
						)}

						{generation.dispatch?.failureReason && (
							<p className='mt-2 text-sm text-red'>
								{generation.dispatch.failureReason}
							</p>
						)}
					</div>

					<div className='flex flex-col items-end gap-3'>
						<GenerationStatusBadge
							status={generation.status}
							error={generation.error}
						/>

						<Flex className='gap-2'>
							{isRunning && (
								<Button
									variant='danger'
									className='!px-3 !py-2'
									onClick={async () => {
										await cancelGeneration({ id: generation.id })
										dispatch(
											notificationCreators.show(
												successMessages.canceledAiGeneration
											)
										)
									}}
								>
									<>Cancelar</>
								</Button>
							)}

							{(generation.status === 'failed' ||
								generation.status === 'canceled') && (
								<Button
									variant='outline'
									className='!px-3 !py-2'
									onClick={async () => {
										await retryGeneration({ id: generation.id })
										dispatch(
											notificationCreators.show(
												successMessages.retriedAiGeneration
											)
										)
									}}
								>
									<>Tentar de novo</>
								</Button>
							)}

							<Button
								variant='outline'
								className='!px-3 !py-2'
								onClick={() => setShowHistory((previous) => !previous)}
							>
								<>{showHistory ? 'Ocultar histórico' : 'Ver histórico'}</>
							</Button>
						</Flex>
					</div>
				</Flex>

				{isRunning && (
					<div className='mt-6'>
						{percentage === null ? (
							<p className='text-sm text-gray'>
								Lendo o caderno. A contagem de questões aparece quando a leitura
								terminar.
							</p>
						) : (
							<>
								<div className='h-2 w-full bg-gray-200 rounded-full overflow-hidden'>
									<div
										className='h-full bg-primary transition-all'
										style={{ width: `${percentage}%` }}
									/>
								</div>
								<p className='mt-2 text-sm text-gray'>
									Convertendo, {percentage}%
								</p>
							</>
						)}
					</div>
				)}
			</div>

			{showHistory && (
				<div className='mt-6 bg-white rounded-sm shadow-5 p-6'>
					<h2 className='text-lg font-medium text-dark'>Histórico</h2>

					{history?.unavailable && (
						<p className='mt-2 text-sm text-yellow'>
							O microserviço não respondeu: {history.unavailable}
						</p>
					)}

					<ul className='mt-4 flex flex-col gap-3'>
						{history?.transitions.map((transition) => (
							<li key={transition.id} className='text-sm text-gray'>
								<span className='text-dark font-medium'>
									{transition.fromStatus
										? `De ${describeGenerationStatus(
												transition.fromStatus
										  ).toLocaleLowerCase(
												'pt-BR'
										  )} para ${describeGenerationStatus(
												transition.toStatus
										  ).toLocaleLowerCase('pt-BR')}`
										: describeGenerationStatus(transition.toStatus)}
								</span>
								<span className='block'>
									{new Date(transition.createdAt).toLocaleString('pt-BR')}
									{transition.reason ? `. ${transition.reason}` : ''}
								</span>
							</li>
						))}

						{!history?.transitions.length && (
							<li className='text-sm text-gray'>Nada registrado ainda.</li>
						)}
					</ul>
				</div>
			)}

			<Flex className='mt-10 items-baseline gap-3'>
				<h2 className='text-lg font-medium text-dark'>Questões geradas</h2>

				{/* Só depois que a tabela já está na tela: no primeiro carregamento o
				    spinner abaixo já diz que algo está vindo. */}
				{fetchingQuestions && !loadingQuestions && (
					<span className='text-sm text-gray'>atualizando...</span>
				)}
			</Flex>

			<p className='mt-2 text-gray max-w-[820px] leading-relaxed'>
				Nenhuma entra no acervo antes da curadoria. Clique na questão para
				abri-la. Os pontos de atenção ao lado apontam onde olhar primeiro.
			</p>

			<div className='mt-6'>
				{loadingQuestions ? (
					<div className='flex justify-center'>
						<Spinner size='md' />
					</div>
				) : (
					<Table
						th={[
							{ label: 'nº' },
							{ label: 'questão' },
							{ label: 'pontos de atenção' },
						]}
					>
						{questions?.data && questions.data.length > 0 ? (
							questions.data.map(({ review, question }) => (
								/*
								 * A linha inteira abre a curadoria. Um botão "Curar" ao lado
								 * repetiria o que a linha já diz, num alvo menor, numa tabela
								 * cuja única ação é essa.
								 */
								<Tr
									key={review.id}
									className={
										question
											? 'cursor-pointer hover:!bg-gray-100 transition-colors'
											: undefined
									}
									onClick={
										question
											? () =>
													navigate(
														`/revisar-questoes/${question.id}?geracao=${generationId}`
													)
											: undefined
									}
								>
									<Td className='px-6 py-5 align-top text-gray'>
										{review.sourceNumber ?? '—'}
									</Td>

									<Td className='px-6 py-5 pr-10 w-[45%] align-top'>
										<span className='font-medium block mb-2 leading-snug'>
											{question?.title || 'Sem título'}
										</span>
										<span className='text-sm text-gray line-clamp-2 leading-relaxed'>
											{question?.description}
										</span>
									</Td>

									<Td className='px-6 py-5 w-[35%] align-top'>
										<ReviewAlerts
											review={review}
											title={question?.title}
											explanation={question?.explanation}
											subMatterCount={question?.subMatters?.length ?? 0}
										/>
									</Td>
								</Tr>
							))
						) : (
							<EmptyRow
								tdAmount={3}
								message={
									isRunning
										? 'As questões aparecem aqui conforme ficam prontas'
										: 'Nenhuma questão chegou desta conversão'
								}
							/>
						)}
					</Table>
				)}
			</div>

			<Divider className='mt-[32px] mb-[12px]' />

			{questions?.data && questions.data.length > 0 && (
				<Pagination
					pageCount={questions.meta.lastPage || 0}
					maxAmount={questions.meta.total || 0}
					perPage={questions.meta.perPage || 0}
				/>
			)}
		</Template>
	)
}

export default GenerationDetail
